import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import Seo, { buildBreadcrumbLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import CtaSection from "../components/CtaSection";
import { api } from "../lib/api";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(undefined); // undefined=loading, null=notfound

  useEffect(() => {
    api.get(`/projects/published/${slug}`).then((r) => setProject(r.data)).catch(() => setProject(null));
  }, [slug]);

  if (project === undefined) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>;
  if (project === null) return <Navigate to="/referenzen" replace />;

  const crumbs = [
    { label: "Startseite", to: "/" },
    { label: "Referenzen", to: "/referenzen" },
    { label: project.title, to: `/referenzen/${project.slug}` },
  ];

  return (
    <div data-testid={`page-project-${project.slug}`}>
      <Seo
        path={`/referenzen/${project.slug}`}
        title={project.seo_title || `${project.title} | Referenzen | Rümpeltaxi`}
        description={project.meta_description || "Vorher-Nachher-Einblick in ein abgeschlossenes Projekt von Rümpeltaxi."}
        jsonLd={buildBreadcrumbLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero
        kicker={[project.object_type, project.service_type].filter(Boolean).join(" · ") || "Referenz"}
        title={project.title}
        lead="Der direkte Bildvergleich zeigt die Veränderung des geräumten Bereichs vor und nach der Durchführung der vereinbarten Leistung."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-page grid gap-12 lg:grid-cols-[1.3fr_0.7fr] lg:gap-16">
          <Reveal>
            {project.before_image && project.after_image ? (
              <BeforeAfterSlider before={project.before_image} after={project.after_image} alt={project.title} />
            ) : (
              <img src={project.after_image} alt={project.alt_after || project.title} className="w-full rounded-md" />
            )}
          </Reveal>
          <Reveal delay={1}>
            <dl className="space-y-5">
              {project.object_type && <div><dt className="text-xs font-semibold uppercase tracking-widest text-mid-gray">Objektart</dt><dd className="mt-1 text-anthracite">{project.object_type}</dd></div>}
              {project.service_type && <div><dt className="text-xs font-semibold uppercase tracking-widest text-mid-gray">Leistungsart</dt><dd className="mt-1 text-anthracite">{project.service_type}</dd></div>}
              {project.region && <div><dt className="text-xs font-semibold uppercase tracking-widest text-mid-gray">Region</dt><dd className="mt-1 text-anthracite">{project.region}</dd></div>}
              {project.situation && <div><dt className="text-xs font-semibold uppercase tracking-widest text-mid-gray">Ausgangssituation</dt><dd className="mt-1 text-mid-gray">{project.situation}</dd></div>}
              {project.result && <div><dt className="text-xs font-semibold uppercase tracking-widest text-mid-gray">Ergebnis</dt><dd className="mt-1 text-mid-gray">{project.result}</dd></div>}
            </dl>
            <Link to="/kontakt" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-yellow px-6 py-3.5 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark" data-testid="project-cta">
              Ähnliches Projekt anfragen <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
